import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FieldType, FormlyModule, type FormlyFieldConfig } from '@ngx-formly/core';
import { MatTabsModule } from '@angular/material/tabs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs';
import { UserPreferenceStore } from '../../../core/services/user-preference/user-preference.store';
import { DETAILS_SERVICE_TOKEN } from '../../../features/admin/modules/modules-entity/templates/details/details/details.service';
import { isOverviewNode, skipsInitialSelection } from './readonly/overview.types';
import { isAnswerField } from './review-value.util';

@Component({
  selector: 'formly-field-tabs',
  template: `
    <mat-tab-group
      [selectedIndex]="index"
      [preserveContent]="!!props.preserveContent"
      animationDuration="0ms"
      (selectedIndexChange)="saveTabSettings($event)">
      @for (tab of visibleTabs(); track trackByFn($index, tab)) {
        <mat-tab>
          <ng-template mat-tab-label>
            <!-- A tab without a key falls back to props.dataCy: the synthetic Overzicht tab
                 (#21239, task 21250) carries no key on purpose, and 'tab-label-undefined' is a
                 hook no spec can rely on. -->
            <span [attr.data-cy]="'tab-label-' + (tab.key ?? tab.props?.dataCy ?? 'onbekend')">{{
              tab.props.label
            }}</span>
            @if (getTabErrorCount(tab.key) > 0) {
              <span
                class="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5
                       text-xs font-semibold rounded-full leading-5 bg-red-500 text-white"
                [attr.aria-label]="getTabErrorCount(tab.key) + ' fouten'">
                {{ getTabErrorCount(tab.key) }}
              </span>
            }
            @if (getPendingDocsCount(tab) > 0) {
              <span
                class="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5
                       text-xs font-semibold leading-5 rounded-full bg-amber-400 text-amber-900"
                [attr.aria-label]="getPendingDocsCount(tab) + ' meldingen'">
                {{ getPendingDocsCount(tab) }}
              </span>
            }
          </ng-template>
          <!-- Lazy rendering with matTabContent ensures proper change detection when switching tabs -->
          <!-- Fields in non-active tabs are created fresh when selected, reading current touched/invalid state -->
          <!-- Exception: props.preserveContent keeps visited tabs alive (opt-in, e.g. form builder settings) -->
          <ng-template matTabContent>
            <formly-field [field]="tab" [formlyAttributes]="field"></formly-field>
          </ng-template>
        </mat-tab>
      }
    </mat-tab-group>

    <!-- Mobile scrollable buttons. Gated by a real breakpoint instead of CSS-only
         (sm:hidden): a display:none copy still instantiates every tab's formly
         subtree, doubling the whole form on desktop. -->
    @if (isHandset()) {
    <div class="block sm:hidden">
      <div class="overflow-x-auto scrollbar-hide">
        <div class="flex gap-2 pb-2 min-w-min px-2">
          @for (tab of visibleTabs(); track trackByFn($index, tab); let i = $index) {
            <button
              [class.bg-primary]="index === i"
              [class.text-white]="index === i"
              (click)="saveTabSettings(i)"
              class="relative px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium
                     transition-colors duration-200
                     focus:outline-none focus:ring-2 focus:ring-primary">
              {{ tab.props?.label }}
              @if (getTabErrorCount(tab.key) > 0) {
                <span
                  class="absolute -top-1 -right-1 min-w-4 h-4 flex items-center justify-center
                         text-[10px] font-semibold rounded-full bg-red-500 text-white"
                  [attr.aria-label]="getTabErrorCount(tab.key) + ' fouten'">
                  {{ getTabErrorCount(tab.key) }}
                </span>
              }
              @if (getPendingDocsCount(tab) > 0) {
                <span
                  class="absolute -top-1 -right-1 min-w-4 h-4 flex items-center justify-center
                         text-[10px] font-semibold rounded-full bg-amber-400 text-amber-900"
                  [attr.aria-label]="getPendingDocsCount(tab) + ' meldingen'">
                  {{ getPendingDocsCount(tab) }}
                </span>
              }
            </button>
          }
        </div>
      </div>

      <div class="mt-4">
        <!-- Only the active tab lives in the DOM (matches the desktop matTabContent
             semantics); [class.hidden] kept all tabs' subtrees instantiated. Hidden
             tabs never reach either viewport: visibleTabs() is the one filter, so the
             buttons above, the panel below and mat-tab-group all count the same list. -->
        @for (tab of visibleTabs(); track trackByFn($index, tab); let i = $index) {
          @if (index === i) {
            <div>
              <formly-field [field]="tab"></formly-field>
            </div>
          }
        }
      </div>
    </div>
    }
  `,
  styles: `
    ::ng-deep formly-field .mat-mdc-tab-group .mat-mdc-tab-header .mat-mdc-tab-label-container {
      margin: 0 !important;
    }
  `,
  imports: [FormlyModule, MatTabsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldTabs extends FieldType implements OnInit {
  index = 0;
  userPreferanceStore = inject(UserPreferenceStore);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  readonly #detailsService = inject(DETAILS_SERVICE_TOKEN, { optional: true });
  // Tailwind `sm` = 640px, matching the sm:hidden class on the mobile block.
  readonly isHandset = toSignal(
    inject(BreakpointObserver)
      .observe('(max-width: 639.98px)')
      .pipe(map(result => result.matches)),
    { initialValue: false }
  );

  /**
   * The tabs the template actually renders — and therefore the ONE counting `index` may use.
   * `[selectedIndex]` and the `(selectedIndexChange)` that feeds `saveTabSettings` count
   * `<mat-tab>` elements, so a tab filtered out here does not occupy a slot. Mixing that with
   * fieldGroup positions is an off-by-one per hidden tab, and it lands exactly where it hurts:
   * with `[overzicht, algemeen(hidden), locaties]` the fieldGroup index of Locaties is 2, but
   * mat-tab-group's index 2 does not exist — index 1 is Locaties.
   *
   * A plain method, not a computed: Formly mutates `field.fieldGroup` in place, outside any
   * reactive context, so a signal would go stale on exactly the mode flip this guards.
   */
  visibleTabs(): FormlyFieldConfig[] {
    return (this.field?.fieldGroup ?? []).filter(tab => tab.className !== 'hidden' && !tab.hide);
  }

  trackByFn(_index: number, item: any): any {
    return item.id;
  }

  ngOnInit() {
    this.index = this.getTabIndex();

    // Subscribe to tab switch requests from detailsService
    if (this.#detailsService?.switchTab$) {
      this.#detailsService.switchTab$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((tabKey: string) => {
        if (this.switchToTab(tabKey)) {
          this.cdr.markForCheck();
        }
      });
    }

    // Subscribe to tab error notifications from detailsService
    if (this.#detailsService?.tabErrorsChanged$) {
      this.#detailsService.tabErrorsChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.cdr.markForCheck();
      });
    }
  }

  /**
   * The tabs the persisted preference counts: `visibleTabs()` minus the synthetic Overzicht node,
   * if present. The node is unshifted whenever the module opts in (`DetailsService.#addOverviewTab`)
   * and carries no key of its own, so letting it occupy a countable slot would shift every stored
   * preference by one — and shift it back the moment the last `inOverview` flag is removed.
   */
  #ownTabs(): FormlyFieldConfig[] {
    return this.visibleTabs().filter(tab => !isOverviewNode(tab));
  }

  /**
   * Where the tabs open, and what a stored preference means.
   *
   * The number `saveTabSettings` persists counts the module's OWN tabs (`#ownTabs()`), never the
   * visible strip `[selectedIndex]` uses — see `#ownTabs()`. So a stored value is first read back
   * as an own-tab index and translated to its position in `visibleTabs()`. A preference saved
   * before this node existed counted the same way (there was nothing to filter out, so own tabs
   * and visible tabs were the same list), which is what keeps it resolving to the same tab.
   *
   * One case overrides the stored value outright: an Overzicht node that is NOT
   * `skipInitialSelection` is the read view's own copy of the node, and the read view opens ON
   * the summary regardless of what was last selected there — see `skipsInitialSelection`.
   */
  getTabIndex(): number {
    const visible = this.visibleTabs();
    const landingNode = visible.find(tab => isOverviewNode(tab) && !skipsInitialSelection(tab));
    if (landingNode) {
      return visible.indexOf(landingNode);
    }

    // `ui` is untyped; a value that is not a usable number (missing, a string, NaN) counts as 0.
    const stored = this.#shouldPersistTabIndex()
      ? Number(this.userPreferanceStore.userPreference()?.ui[this.field.id]) || 0
      : 0;

    // Without a node `#ownTabs()` IS `visibleTabs()`, so this is the identity for any in-range
    // value. A stored index that points past the tabs (a tab removed since, a role that hides it)
    // falls back to the first real tab instead of leaking out as a selection of nothing — the
    // mobile panel renders `@if (index === i)` and would otherwise show a blank form.
    const ownTab = this.#ownTabs()[stored];
    return ownTab ? visible.indexOf(ownTab) : this.#firstInitialIndex();
  }

  /** Where the tabs open: the first tab that is not flagged to be skipped on init. */
  #firstInitialIndex(): number {
    const index = this.visibleTabs().findIndex(tab => !skipsInitialSelection(tab));
    return index >= 0 ? index : 0;
  }

  saveTabSettings(index: number) {
    this.index = index;
    this.cdr.markForCheck();

    if (!this.#shouldPersistTabIndex()) {
      return;
    }

    // Persist the position among the module's OWN tabs (`#ownTabs()`); without a node that is
    // the visible index itself, so preferences written before the node existed keep their meaning.
    const tab = this.visibleTabs()[index];
    if (isOverviewNode(tab)) {
      // The summary is never a remembered destination: it has no position of its own among the
      // module's own tabs to persist against.
      return;
    }
    const ownIndex = this.#ownTabs().indexOf(tab);
    if (ownIndex < 0) {
      return;
    }

    // Update local state immediately for responsive UI
    this.userPreferanceStore.editUIImmediate({
      [this.field.id]: ownIndex,
    });
    // Debounced API call to update the server
    this.userPreferanceStore.editUIDebounced({
      [this.field.id]: ownIndex,
    });
  }

  /**
   * Persistence is opt-out via props.persistSelectedTab === false — set on the
   * admin form-builder settings tabs, whose runtime field.id is regenerated every
   * build so a stored index is never read back. NOT inferred from the id shape:
   * saved module definitions legitimately carry stable ids like
   * 'formly_4291_input_id_1', so a 'formly_' prefix check would break real entry
   * pages. Also skipped for the admin form preview and unsaved entries.
   */
  #shouldPersistTabIndex(): boolean {
    if (this.props.persistSelectedTab === false) return false;
    if (this.options.formState?.adminPreview) return false;
    return !!this.options.formState?.entry?.id;
  }

  /**
   * Get the error count for a specific tab from detailsService signal
   */
  getTabErrorCount(tabKey: string | number | undefined): number {
    if (!tabKey) return 0;
    if (!this.#detailsService?.tabErrorCounts) return 0;
    const tabErrors = this.#detailsService.tabErrorCounts() as Map<string, number>;
    return tabErrors?.get(String(tabKey)) || 0;
  }

  /**
   * The tab badge count (#21239-follow-up): the sum of every registered notice's `count` whose
   * sourceKey resolves to a field keyPath actually on THIS tab — the generic replacement for the
   * old single-purpose `pendingDocsCount` signal, which only ever knew about one hardcoded
   * documents warning. `DetailsService.noticesCountForKeyPaths` does the matching; this method's
   * own job is collecting the tab's own field keyPaths, the same way a publisher derives its own
   * sourceKey (`props.keyPath ?? key`) — so a notice only counts here when it belongs to a field
   * that is actually somewhere on this tab, never another tab's.
   *
   * Walks the WHOLE subtree, not just the direct children: a publishing field can sit nested
   * inside a layout `group` (or any other container), and its keyPath must still count toward the
   * tab it visually lives on.
   *
   * The walk runs ONCE per tab object (`#keyPathsByTab`), not once per call: the template binds
   * this method three times per tab on every change-detection pass, and a form's `formValue`
   * changes on every keystroke, so re-walking ~30 fields per tab per pass allocated a fresh `Set`
   * on every key press. The keyPaths are structural config — `key`/`props.keyPath` are written by
   * the form generator, never by an expression — so they cannot change under the same tab object;
   * a rebuilt field tree hands this method new tab objects, which the `WeakMap` simply misses on.
   * Only the registry lookup itself stays on the per-pass path.
   */
  getPendingDocsCount(tab: FormlyFieldConfig): number {
    if (!this.#detailsService?.noticesCountForKeyPaths) return 0;
    const keyPaths = this.#keyPathsOf(tab);
    if (keyPaths.size === 0) return 0;
    return this.#detailsService.noticesCountForKeyPaths(keyPaths);
  }

  readonly #keyPathsByTab = new WeakMap<FormlyFieldConfig, ReadonlySet<string>>();

  #keyPathsOf(tab: FormlyFieldConfig): ReadonlySet<string> {
    let keyPaths = this.#keyPathsByTab.get(tab);
    if (!keyPaths) {
      keyPaths = new Set(this.#collectKeyPaths(tab.fieldGroup ?? []));
      this.#keyPathsByTab.set(tab, keyPaths);
    }
    return keyPaths;
  }

  /**
   * Depth-first walk collecting every descendant ANSWER field's own keyPath (`props.keyPath ??
   * key`) — `isAnswerField` (same predicate `overview.types.ts`'s `collectFlagged` uses) is what
   * keeps a plain layout `group`'s own key (e.g. `'block'`) from being collected as if it were a
   * publishing field's keyPath; the walk still descends INTO the group's `fieldGroup` regardless.
   */
  #collectKeyPaths(fields: FormlyFieldConfig[]): string[] {
    const keyPaths: string[] = [];
    for (const field of fields) {
      if (isAnswerField(field)) {
        const keyPath =
          (field.props?.['keyPath'] as string | undefined) ?? (typeof field.key === 'string' ? field.key : undefined);
        if (keyPath) keyPaths.push(keyPath);
      }
      if (field.fieldGroup) keyPaths.push(...this.#collectKeyPaths(field.fieldGroup));
    }
    return keyPaths;
  }

  /**
   * Switch to a specific tab by its key
   * Returns true if the tab was found and switched to
   */
  public switchToTab(tabKey: string): boolean {
    if (!this.field.fieldGroup) return false;

    // Over visibleTabs(), because the result is written straight into `index`.
    const tabIndex = this.visibleTabs().findIndex(tab => String(tab.key) === tabKey);

    if (tabIndex >= 0) {
      this.saveTabSettings(tabIndex);
      return true;
    }

    return false;
  }
}
